/**
 * Linters du GS1 Barcode Syntax Dictionary (ADR-020 §3), transcrits en TypeScript depuis les
 * implémentations de référence en C du dépôt gs1/gs1-syntax-dictionary (Apache-2.0, commit
 * c63d8a12210dd0cfaceac4aae45ccf57b48eb15b ; voir data/NOTICE-gs1.md). Les listes ISO 3166,
 * ISO 4217, types de média et types d'emballage sont les champs de bits et tables de ces linters.
 *
 * Un linter renvoie `null` si la valeur est conforme, sinon un motif en français. Un linter
 * inconnu est une erreur explicite : une valeur n'est jamais acceptée faute de savoir la vérifier.
 *
 * Seule tolérance documentée : `gcppos1` et `gcppos2` vérifient, comme la référence sans base de
 * préfixes, que les quatre premiers chiffres du préfixe d'entreprise sont numériques ; aucune
 * consultation d'un registre de préfixes GS1 n'est faite (aucune dépendance réseau, ADR-020).
 */
const isDigit = (char) => char !== undefined && char >= '0' && char <= '9';
const allDigits = (data) => [...data].every((char) => isDigit(char));
const NON_DIGIT = 'un caractère non numérique est présent';
/** Lecture d'un champ de bits de la référence : mots de 64 bits, bit de poids fort = première valeur. */
function bitfield(words, value) {
    const word = words[Math.floor(value / 64)];
    if (word === undefined)
        return false;
    return ((word >> BigInt(63 - (value % 64))) & 1n) === 1n;
}
const ISO3166 = [
    0x08a888898888b888n, 0x8aa80a2888888888n, 0x0888888a22232889n, 0x88188a2221e322a2n,
    0x2a2a180088888888n, 0x888a888888888888n, 0x888288a2622a22a2n, 0x222222228808b888n,
    0x8888970808222222n, 0x2de102888888a222n, 0x320a1b222222a203n, 0xe20808c8088888a8n,
    0x8888889a89002021n, 0xe080222a00082102n, 0x0000000000000000n, 0x0000000000000000n,
];
const ISO3166_ALPHA2 = [
    0x1e9afb77f7bdbb7bn, 0xe4fc21a8012b0070n, 0x003a900df9dfa800n, 0xb160181ef00202c0n,
    0x00b8d42f8281f2bfn, 0x3fffeba4d2100080n, 0x023cf1ca80000002n, 0x008a8fbfe75cddf9n,
    0x59820820eaa10200n, 0x4002000000000800n, 0x1020020080000000n,
];
const ISO4217 = [
    0x008800008808b808n, 0x8880082080880808n, 0x0880808800220008n, 0x8010820202822000n,
    0x0202000008000000n, 0x8888088888888080n, 0x088a88a262222002n, 0x022200008800a080n,
    0x88080c0008220200n, 0x2022028880000020n, 0x1202000000202002n, 0xa200008000088888n,
    0x0088880081002020n, 0x2080002800002200n, 0x0400000fe6adbfdfn, 0xfdfdfce225000000n,
];
/** Types de média AIDC (AI 7241) : 01-10 et 80-99, champ de bits sur 8 bits de la référence. */
const MEDIA_TYPES = [0x7f, 0xe0, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xff, 0xff, 0xf0];
const PACKAGE_TYPES = new Set(('1A 1B 1D 1F 1G 1W 200 201 202 203 204 205 206 210 211 212 2C 3A 3H 43 44 4A 4B 4C 4D 4F 4G 4H 5H 5L 5M 6H 6P 7A 7B 8 8A 8B 8C 9 ' +
    'AA AB AC AD AF AG AH AI AJ AL AM AP APE AT AV B4 BB BC BD BE BF BG BGE BH BI BJ BK BL BM BME BN BO BP BQ BR BRI BS BT BU BV BW BX BY BZ ' +
    'CA CB CBL CC CCE CD CE CF CG CH CI CJ CK CL CM CN CO CP CQ CR CS CT CU CV CW CX CY CZ DA DB DC DG DH DI DJ DK DL DM DN DP DPE DR DS DT DU DV DW DX DY ' +
    'E1 E2 E3 EC ED EE EF EG EH EI EN FB FC FD FE FI FL FO FOB FP FPE FR FT FW FX GB GI GL GR GU GY GZ HA HB HC HG HN HR ' +
    'IA IB IC ID IE IF IG IH IK IL IN IZ JB JC JG JR JT JY KG KI LAB LE LG LT LU LV LZ MA MB MC ME MPE MR MS MT MW MX NA NE NF NG NS NT NU NV ' +
    'OA OB OC OD OE OF OK OPE OT OU P2 PA PAE PB PC PD PE PF PG PH PI PJ PK PL PLP PN PO POP PP PPE PR PT PU PUE PV PX PY PZ ' +
    'QA QB QC QD QF QG QH QJ QK QL QM QN QP QQ QR QS RB1 RB2 RB3 RCB RD RG RJ RK RL RO RT RZ ' +
    'S1 SA SB SC SD SE SEC SH SI SK SL SM SO SP SS ST STL SU SV SW SX SY SZ T1 TB TC TD TE TEV TG THE TI TK TL TN TO TR TRE TS TT TTE TU TV TW TWE TY TZ ' +
    'UC UN UUE VA VG VI VK VL VN VO VP VQ VR VS VY WA WB WC WD WF WG WH WJ WK WL WM WN WP WQ WR WRP WS WT WU WV WW WX WY WZ ' +
    'X11 X12 X15 X16 X17 X18 X19 X20 X3 XA XB XC XD XF XG XH XJ XK YA YB YC YD YF YG YH YJ YK YL YM YN YP YQ YR YS YT YV YW YX YY YZ ' +
    'ZA ZB ZC ZD ZF ZG ZH ZJ ZK ZL ZM ZN ZP ZQ ZR ZS ZT ZU ZV ZW ZX ZY ZZ').split(' '));
const CSET82_WEIGHTS = new Map([..."!\"%&'()*+,-./0123456789:;<=>?ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz"].map((char, index) => [char, index + 1]));
const CSET32 = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
const PRIMES = [
    2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61, 67, 71, 73, 79, 83, 89, 97, 101, 103, 107, 109, 113, 127, 131, 137, 139, 149, 151,
    157, 163, 167, 173, 179, 181, 191, 193, 197, 199, 211, 223, 227, 229, 233, 239, 241, 251, 257, 263, 269, 271, 277, 281, 283, 293, 307, 311, 313, 317,
    331, 337, 347, 349, 353, 359, 367, 373, 379, 383, 389, 397, 401, 409, 419, 421, 431, 433, 439, 443, 449, 457, 461, 463, 467, 479, 487, 491, 499, 503, 509,
];
/** Chiffre de contrôle modulo 10 des clés GS1 (GTIN, SSCC, GLN…). */
export const csum = (data) => {
    if (data.length === 0)
        return 'trop court pour porter un chiffre de contrôle';
    if (!allDigits(data))
        return NON_DIGIT;
    let weight = data.length % 2 === 0 ? 3 : 1;
    let parity = 0;
    for (let position = 0; position < data.length - 1; position += 1) {
        parity += weight * Number(data[position]);
        weight = 4 - weight;
    }
    return (10 - (parity % 10)) % 10 === Number(data[data.length - 1]) ? null : 'le chiffre de contrôle est faux';
};
/** Paire de caractères de contrôle alphanumérique (GMN, AI 8013). */
const csumalpha = (data) => {
    if (data.length > PRIMES.length + 2)
        return 'trop long pour la paire de contrôle';
    if (data.length < 2)
        return 'trop court pour porter une paire de contrôle';
    if (data.length === 2)
        return data === '22' ? null : 'la paire de contrôle est fausse';
    let sum = 0;
    for (let position = 0; position < data.length - 2; position += 1) {
        const weight = CSET82_WEIGHTS.get(data[position]);
        if (weight === undefined)
            return 'un caractère hors CSET 82 est présent';
        sum += (weight - 1) * PRIMES[data.length - 3 - position];
    }
    sum %= 1021;
    return data[data.length - 2] === CSET32[sum >> 5] && data[data.length - 1] === CSET32[sum & 31] ? null : 'la paire de contrôle est fausse';
};
const gcppos1 = (data) => {
    if (data.length < 4)
        return 'trop court pour un préfixe d’entreprise GS1';
    return allDigits(data.slice(0, 4)) ? null : 'le préfixe d’entreprise GS1 doit commencer par quatre chiffres';
};
const gcppos2 = (data) => (data.length < 2 ? 'trop court pour un préfixe d’entreprise GS1' : gcppos1(data.slice(1)));
const yyyymmd0 = (data) => {
    if (data.length !== 8)
        return data.length < 8 ? 'date trop courte' : 'date trop longue';
    if (!allDigits(data))
        return NON_DIGIT;
    const year = Number(data.slice(0, 4));
    const month = Number(data.slice(4, 6));
    const day = Number(data.slice(6, 8));
    if (month < 1 || month > 12)
        return 'mois de l’année illégal';
    const days = month === 2 ? ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28) : [31, 0, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
    return day > days ? 'jour du mois illégal' : null;
};
const yyyymmdd = (data) => yyyymmd0(data) ?? (data.slice(6, 8) === '00' ? 'jour du mois illégal' : null);
/** Fenêtre de siècle des dates à deux chiffres (GS1 General Specifications §7.12), sur l'année courante. */
const yymmd0 = (data) => {
    if (data.length !== 6)
        return data.length < 6 ? 'date trop courte' : 'date trop longue';
    if (!allDigits(data))
        return NON_DIGIT;
    const current = new Date().getUTCFullYear() % 100;
    const difference = Number(data.slice(0, 2)) - current;
    const century = difference >= 51 ? '19' : difference > -50 ? '20' : '21';
    return yyyymmd0(`${century}${data}`);
};
const yymmdd = (data) => yymmd0(data) ?? (data.slice(4, 6) === '00' ? 'jour du mois illégal' : null);
const twoDigits = (limit, label) => (data) => {
    if (data.length !== 2)
        return `${label} : deux chiffres attendus`;
    if (!allDigits(data))
        return NON_DIGIT;
    return Number(data) > limit ? `${label} illégale` : null;
};
const hh = twoDigits(23, 'heure');
const mi = twoDigits(59, 'minute');
const ss = twoDigits(59, 'seconde');
const hhmi = (data) => (data.length !== 4 ? 'heure et minute : quatre chiffres attendus' : (hh(data.slice(0, 2)) ?? mi(data.slice(2))));
const iso3166 = (data) => (data.length === 3 && allDigits(data) && bitfield(ISO3166, Number(data)) ? null : 'un code pays ISO 3166 numérique valide est requis');
const iso3166999 = (data) => (data === '999' || iso3166(data) === null ? null : 'un code pays ISO 3166 numérique valide ou 999 est requis');
const iso3166alpha2 = (data) => data.length === 2 && /^[A-Z]{2}$/.test(data) && bitfield(ISO3166_ALPHA2, (data.charCodeAt(0) - 65) * 26 + data.charCodeAt(1) - 65) ? null : 'un code pays ISO 3166 alpha-2 valide est requis';
const iso4217 = (data) => (data.length === 3 && allDigits(data) && bitfield(ISO4217, Number(data)) ? null : 'un code de devise ISO 4217 numérique valide est requis');
const iso5218 = (data) => (data.length === 1 && '0129'.includes(data) ? null : 'code de sexe biologique ISO 5218 invalide');
const pcenc = (data) => {
    for (let position = 0; position < data.length; position += 1) {
        if (data[position] !== '%')
            continue;
        if (position + 2 >= data.length || !/^[0-9A-Fa-f]{2}$/.test(data.slice(position + 1, position + 3)))
            return 'séquence d’encodage pour-cent invalide';
        position += 2;
    }
    return null;
};
const yesno = (data) => (data === '0' || data === '1' ? null : '0 ou 1 attendu');
const zero = (data) => (data.length > 0 && /^0+$/.test(data) ? null : 'zéro attendu');
const nonzero = (data) => (!allDigits(data) ? NON_DIGIT : /^0*$/.test(data) ? 'une valeur nulle est interdite' : null);
const winding = (data) => (data === '0' || data === '1' || data === '9' ? null : 'sens d’enroulement invalide');
const hyphen = (data) => (data.length > 0 && /^-+$/.test(data) ? null : 'trait d’union attendu');
const nozeroprefix = (data) => (data.startsWith('0') ? 'zéro de tête interdit' : allDigits(data) ? null : NON_DIGIT);
const hasnondigit = (data) => (allDigits(data) ? 'au moins un caractère non numérique est requis' : null);
const pieceoftotal = (data) => {
    if (!allDigits(data))
        return NON_DIGIT;
    if (data.length === 0 || data.length % 2 !== 0)
        return 'longueur invalide pour « pièce sur total »';
    const piece = data.slice(0, data.length / 2);
    const total = data.slice(data.length / 2);
    if (/^0+$/.test(piece))
        return 'numéro de pièce nul';
    if (/^0+$/.test(total))
        return 'total de pièces nul';
    return piece > total ? 'le numéro de pièce dépasse le total' : null;
};
const latitude = (data) => (data.length !== 10 ? 'latitude : dix chiffres attendus' : !allDigits(data) ? NON_DIGIT : Number(data) > 1_800_000_000 ? 'latitude invalide' : null);
const longitude = (data) => (data.length !== 10 ? 'longitude : dix chiffres attendus' : !allDigits(data) ? NON_DIGIT : Number(data) > 3_600_000_000 ? 'longitude invalide' : null);
const IBAN_WEIGHTS = new Map([...'0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'].map((char, index) => [char, index + 1]));
const iban = (data) => {
    if (data.length < 4)
        return 'IBAN trop court';
    if (iso3166alpha2(data.slice(0, 2)) !== null)
        return 'code pays d’IBAN illégal';
    if (data.length > 34)
        return 'IBAN trop long';
    if (data.length <= 10)
        return 'IBAN trop court';
    let checksum = 0;
    for (let position = 4; position < data.length + 4; position += 1) {
        const weight = IBAN_WEIGHTS.get(data[position < data.length ? position : position - data.length]);
        if (weight === undefined)
            return 'caractère d’IBAN invalide';
        checksum = (checksum * (weight <= 10 ? 10 : 100) + weight - 1) % 97;
    }
    return checksum === 1 ? null : 'clé de contrôle d’IBAN fausse';
};
const mediatype = (data) => {
    if (data.length !== 2 || !allDigits(data))
        return 'type de média AIDC invalide';
    const value = Number(data);
    return ((MEDIA_TYPES[Math.floor(value / 8)] ?? 0) >> (7 - (value % 8))) & 1 ? null : 'type de média AIDC invalide';
};
const packagetype = (data) => (PACKAGE_TYPES.has(data) ? null : 'type d’emballage invalide');
const importeridx = (data) => (data.length === 1 && /^[-0-9A-Z_a-z]$/.test(data) ? null : 'indice d’importateur invalide (un caractère)');
const posinseqslash = (data) => {
    const slash = data.indexOf('/');
    if (slash <= 0 || slash >= data.length - 1 || !allDigits(data.slice(0, slash)) || !allDigits(data.slice(slash + 1)))
        return 'position dans la séquence mal formée';
    const position = data.slice(0, slash);
    const end = data.slice(slash + 1);
    if (position.startsWith('0') || end.startsWith('0'))
        return 'zéro de tête interdit';
    if (position.length > end.length || (position.length === end.length && position > end))
        return 'la position dépasse la fin de la séquence';
    return null;
};
/** Lecture séquentielle d'une valeur de coupon (AI 8110, 8112), transcrite de la référence. */
class CouponReader {
    data;
    position = 0;
    constructor(data) {
        this.data = data;
    }
    get done() {
        return this.position >= this.data.length;
    }
    peek() {
        return this.data[this.position];
    }
    remaining() {
        return this.data.length - this.position;
    }
}
function couponGcp(reader, length) {
    if (reader.remaining() < length)
        return 'coupon : préfixe d’entreprise tronqué';
    const problem = gcppos1(reader.data.slice(reader.position, reader.position + length));
    reader.position += length;
    return problem === null ? null : `coupon : ${problem}`;
}
/** Exigence d'achat de coupon : longueur variable 1-5, code, code de famille de trois chiffres. */
function couponPurchase(reader, label) {
    if (reader.done)
        return `coupon : ${label} manquante`;
    const vli = reader.peek();
    if (vli < '1' || vli > '5')
        return `coupon : longueur de ${label} invalide`;
    reader.position += 1;
    if (reader.remaining() < Number(vli))
        return `coupon : ${label} tronquée`;
    reader.position += Number(vli);
    if (reader.done)
        return `coupon : code de ${label} manquant`;
    const code = reader.peek();
    if (code > '4' && code !== '9')
        return `coupon : code de ${label} invalide`;
    reader.position += 1;
    if (reader.remaining() < 3)
        return `coupon : code de famille de ${label} tronqué`;
    reader.position += 3;
    return null;
}
function couponOptionalGcp(reader, label) {
    if (reader.done)
        return `coupon : longueur du préfixe de ${label} manquante`;
    const vli = reader.peek();
    if (vli > '6' && vli !== '9')
        return `coupon : longueur du préfixe de ${label} invalide`;
    reader.position += 1;
    return vli === '9' ? null : couponGcp(reader, Number(vli) + 6);
}
const couponcode = (data) => {
    if (!allDigits(data))
        return NON_DIGIT;
    const reader = new CouponReader(data);
    if (reader.done)
        return 'coupon : longueur du préfixe d’entreprise manquante';
    if (reader.peek() > '6')
        return 'coupon : longueur du préfixe d’entreprise invalide';
    const gcpLength = Number(reader.peek()) + 6;
    reader.position += 1;
    const gcp = couponGcp(reader, gcpLength);
    if (gcp !== null)
        return gcp;
    if (reader.remaining() < 6)
        return 'coupon : code d’offre tronqué';
    reader.position += 6;
    if (reader.done)
        return 'coupon : longueur de la valeur de remise manquante';
    const saveLength = reader.peek();
    if (saveLength < '1' || saveLength > '5')
        return 'coupon : longueur de la valeur de remise invalide';
    reader.position += 1;
    if (reader.remaining() < Number(saveLength))
        return 'coupon : valeur de remise tronquée';
    reader.position += Number(saveLength);
    const first = couponPurchase(reader, 'première exigence d’achat');
    if (first !== null)
        return first;
    if (reader.peek() === '1') {
        reader.position += 1;
        if (reader.done)
            return 'coupon : code de règles d’achat supplémentaires manquant';
        if (reader.peek() > '3')
            return 'coupon : code de règles d’achat supplémentaires invalide';
        reader.position += 1;
        const second = couponPurchase(reader, 'deuxième exigence d’achat');
        if (second !== null)
            return second;
        const gcp2 = couponOptionalGcp(reader, 'la deuxième exigence');
        if (gcp2 !== null)
            return gcp2;
    }
    if (reader.peek() === '2') {
        reader.position += 1;
        const third = couponPurchase(reader, 'troisième exigence d’achat');
        if (third !== null)
            return third;
        const gcp3 = couponOptionalGcp(reader, 'la troisième exigence');
        if (gcp3 !== null)
            return gcp3;
    }
    let expiry;
    if (reader.peek() === '3') {
        reader.position += 1;
        if (reader.remaining() < 6)
            return 'coupon : trop court pour une date d’expiration';
        expiry = data.slice(reader.position, reader.position + 6);
        if (yymmdd(expiry) !== null)
            return 'coupon : date d’expiration invalide';
        reader.position += 6;
    }
    if (reader.peek() === '4') {
        reader.position += 1;
        if (reader.remaining() < 6)
            return 'coupon : trop court pour une date de début';
        const start = data.slice(reader.position, reader.position + 6);
        if (yymmdd(start) !== null)
            return 'coupon : date de début invalide';
        if (expiry !== undefined && start > expiry)
            return 'coupon : expiration antérieure au début';
        reader.position += 6;
    }
    if (reader.peek() === '5') {
        reader.position += 1;
        if (reader.done)
            return 'coupon : longueur du numéro de série manquante';
        const serialLength = Number(reader.peek()) + 6;
        reader.position += 1;
        if (reader.remaining() < serialLength)
            return 'coupon : numéro de série tronqué';
        reader.position += serialLength;
    }
    if (reader.peek() === '6') {
        reader.position += 1;
        if (reader.done)
            return 'coupon : longueur du préfixe ou GLN du distributeur manquante';
        const vli = reader.peek();
        if (vli < '1' || vli > '7')
            return 'coupon : longueur du préfixe ou GLN du distributeur invalide';
        reader.position += 1;
        const retailer = couponGcp(reader, Number(vli) + 6);
        if (retailer !== null)
            return retailer;
    }
    if (reader.peek() === '9') {
        reader.position += 1;
        if (reader.done)
            return 'coupon : code de valeur de remise manquant';
        if (!'01256'.includes(reader.peek()))
            return 'coupon : code de valeur de remise invalide';
        reader.position += 1;
        if (reader.done)
            return 'coupon : article d’application de la remise manquant';
        if (reader.peek() > '2')
            return 'coupon : article d’application de la remise invalide';
        reader.position += 1;
        if (reader.done)
            return 'coupon : indicateur de coupon de magasin manquant';
        reader.position += 1;
        if (reader.done)
            return 'coupon : indicateur « ne pas multiplier » manquant';
        if (reader.peek() !== '0' && reader.peek() !== '1')
            return 'coupon : indicateur « ne pas multiplier » invalide';
        reader.position += 1;
    }
    return reader.done ? null : 'coupon : données en excès';
};
const couponposoffer = (data) => {
    if (!allDigits(data))
        return NON_DIGIT;
    const reader = new CouponReader(data);
    if (reader.done)
        return 'offre de coupon : code de format manquant';
    if (reader.peek() !== '0' && reader.peek() !== '1')
        return 'offre de coupon : code de format invalide';
    reader.position += 1;
    if (reader.done)
        return 'offre de coupon : longueur du financeur manquante';
    if (reader.peek() > '6')
        return 'offre de coupon : longueur du financeur invalide';
    const funderLength = Number(reader.peek()) + 6;
    reader.position += 1;
    if (reader.remaining() < funderLength)
        return 'offre de coupon : financeur tronqué';
    reader.position += funderLength;
    if (reader.remaining() < 6)
        return 'offre de coupon : code d’offre tronqué';
    reader.position += 6;
    if (reader.done)
        return 'offre de coupon : longueur du numéro de série manquante';
    const serialLength = Number(reader.peek()) + 6;
    reader.position += 1;
    if (reader.remaining() < serialLength)
        return 'offre de coupon : numéro de série tronqué';
    reader.position += serialLength;
    return reader.done ? null : 'offre de coupon : données en excès';
};
export const GS1_LINTERS = Object.freeze({
    csum, csumalpha, gcppos1, gcppos2,
    yymmd0, yymmdd, yyyymmdd, hh, mi, ss, hhmi,
    iso3166, iso3166999, iso3166alpha2, iso4217, iso5218,
    pcenc, yesno, zero, nonzero, winding, hyphen, nozeroprefix, hasnondigit, pieceoftotal,
    latitude, longitude, iban, mediatype, packagetype, importeridx, posinseqslash,
    couponcode, couponposoffer,
});
/** Applique un linter nommé ; un nom inconnu lève une erreur (jamais d'acceptation silencieuse). */
export function runLinter(name, data) {
    const linter = GS1_LINTERS[name];
    if (linter === undefined)
        throw new Error(`Linter GS1 inconnu : « ${name} ».`);
    return linter(data);
}
//# sourceMappingURL=linters.js.map